import React from "react";
import { Link, useNavigate } from "react-router-dom";
import PhoneMockup from "../components/PhoneMockup";
import SEO from "../components/SEO";
import { API } from "../config";

const Home = () => {
  const navigate = useNavigate();

  const fundingOptions = [
    {
      id: "line-of-credit",
      title: "Business Line of Credit",
      amount: "Up to $400K",
      term: "12 to 24 months",
      description:
        "Flexible revolving credit to manage cash flow and seize opportunities as they arise.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"
          />
        </svg>
      ),
    },
    {
      id: "term-loans",
      title: "Term Loans",
      amount: "Up to $5M",
      term: "1 - 10 years",
      description:
        "Long-term financing for major investments, equipment, or business expansion.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75Z"
          />
        </svg>
      ),
    },
    {
      id: "merchant-cash-advance",
      title: "Merchant Cash Advance",
      amount: "$5K - $20M",
      term: "Up to 24 months",
      description:
        "Quick funding based on future sales. Ideal for businesses with consistent revenue.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      ),
    },
    {
      id: "debt-consolidation",
      title: "Debt Consolidation",
      amount: "Up to $3M",
      term: "Up to 24 months",
      description:
        "Simplify your payments by combining multiple debts into one manageable plan.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
          />
        </svg>
      ),
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Complete the application.",
      description:
        "Fill out a quick form with basic business info - takes just minutes.",
    },
    {
      number: "2",
      title: "Fast approval decision.",
      description:
        "Our team reviews your application and presents your best options.",
    },
    {
      number: "3",
      title: "Receive funding.",
      description: "Accept your offer and get funded as soon as the same day.",
    },
  ];

  return (
    <>
      <SEO
        title="Fast Business Funding"
        description="Get business funding in as little as 24 hours. Lloyd Funding offers term loans, lines of credit, and merchant cash advances up to $20M. Apply online today."
        canonical="/"
      />
      {/* Hero Section */}
      <section className="relative bg-white isolate">
        <div className="relative mx-auto max-w-7xl px-4 pt-28 pb-20 sm:px-6 sm:pt-28 sm:pb-24 lg:px-8 lg:pt-32 lg:pb-32">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
            <div>
              <div className="max-w-prose md:max-w-none text-center lg:text-left">
                <h1 className="text-4xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl text-pretty">
                  Small Business Funding{" "}
                  <span className="text-blue-700">made simple</span>
                </h1>
                <p className="mt-6 text-lg text-slate-600 leading-relaxed text-pretty">
                  Get the capital your business needs to grow, expand, or manage
                  cash flow. Lloyd Funding offers flexible financing solutions
                  with quick approvals and competitive rates tailored to your
                  business needs.
                </p>

                <div className="mt-8 flex justify-center lg:justify-start">
                  <Link
                    to="/contact#application-form"
                    className="inline-flex w-full sm:w-auto items-center justify-center rounded-xl bg-blue-600 px-8 py-4 sm:py-3 text-base sm:text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95"
                  >
                    Get Started Today
                  </Link>
                </div>

                {/* Trust Indicators */}
                <div className="mt-10 flex flex-nowrap items-center justify-center lg:justify-start gap-1.5 min-[400px]:gap-2 sm:gap-4 lg:gap-6 text-[11px] min-[400px]:text-xs sm:text-sm text-slate-500">
                  {[
                    "3-Hour Approval",
                    "$5K - $20M Funding",
                    "No Hard Inquiries",
                  ].map((t) => (
                    <div
                      key={t}
                      className="flex items-center gap-0.5 min-[400px]:gap-1 sm:gap-2"
                    >
                      <svg
                        className="h-3.5 w-3.5 min-[400px]:h-4 min-[400px]:w-4 sm:h-5 sm:w-5 text-emerald-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="whitespace-nowrap">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Hero image */}
            <div className="relative">
              <div className="absolute -inset-6 rounded-[28px] bg-blue-200/20 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl shadow-xl ring-1 ring-slate-200/60">
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-slate-900/10 via-transparent to-white/10 z-10" />
                <div className="aspect-[5/4] sm:aspect-[5/4] lg:aspect-[4/3] w-full">
                  <img
                    src="/Hero5.webp"
                    alt="Small business owners interacting with a customer"
                    className="h-full w-full object-cover contrast-[1.05] saturate-[1.05]"
                    width={800}
                    height={600}
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none relative h-10 overflow-hidden">
          <svg
            className="absolute bottom-0 left-0 h-16 w-full"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C288,110 576,110 864,80 C1152,50 1296,50 1440,80 L1440,120 L0,120 Z"
              fill="#F8FAFC"
            />
          </svg>
        </div>
      </section>

      {/* Funding Types Section */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-lg text-center">
            <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
              Our Funding Solutions
            </h2>
            <p className="mt-4 text-lg text-slate-600 text-pretty">
              Whether you need working capital, expansion funds, or debt relief,
              we have flexible financing options to help your business thrive.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {fundingOptions.map((option) => (
              <div
                key={option.title}
                className="group rounded-2xl border border-slate-100 bg-white p-4 lg:p-6 shadow-sm transition-shadow duration-200 hover:shadow-xl hover:ring-1 hover:ring-blue-100"
              >
                <div className="inline-flex rounded-2xl bg-blue-50 p-3 text-blue-600 transition-transform duration-200 md:group-hover:scale-105">
                  {option.icon}
                </div>
                <h3 className="mt-3 text-lg font-semibold text-slate-800">
                  {option.title}
                </h3>
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-medium text-blue-700">
                    {option.amount}
                  </p>
                  <p className="text-sm text-slate-500">{option.term}</p>
                </div>
                <p className="mt-2 text-sm text-slate-600 text-pretty">
                  {option.description}
                </p>
                <Link
                  to={`/services#${option.id}`}
                  className="mt-4 inline-flex items-center justify-center rounded-lg bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition-colors duration-200 group-hover:bg-blue-600 group-hover:text-white"
                >
                  Learn more
                  <svg
                    className="ml-1 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                    />
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none relative h-10 overflow-hidden">
          <svg
            className="absolute bottom-0 left-0 h-16 w-full"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C288,110 576,110 864,80 C1152,50 1296,50 1440,80 L1440,120 L0,120 Z"
              fill="#FFFFFF"
            />
          </svg>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
            {/* Left side - Phone Mockup */}
            <PhoneMockup />

            {/* Right side - Steps */}
            <div>
              <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
                How it works
              </h2>

              <div className="mt-8 space-y-6">
                {steps.map((step) => (
                  <div key={step.number} className="flex gap-4">
                    {/* Number circle */}
                    <div className="flex-shrink-0">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-lg">
                        {step.number}
                      </div>
                    </div>

                    {/* Content */}
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">
                        {step.title}
                      </h3>
                      <p className="mt-1 text-slate-600 text-pretty">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-8">
                <Link
                  to="/contact#application-form"
                  className="inline-flex w-full sm:w-auto items-center justify-center rounded-xl bg-blue-600 px-8 py-4 sm:py-3 text-base sm:text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95"
                >
                  Apply Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Minimum Requirements Section */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
              See if you qualify
            </h2>
            <p className="mt-3 text-lg text-slate-600 text-pretty">
              Here's what we typically look for.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { value: "6+ Months", label: "in business" },
              { value: "Business", label: "bank account" },
              { value: "$10K+", label: "monthly revenue" },
              { value: "500+", label: "credit score" },
            ].map((req) => (
              <div
                key={req.label}
                className="rounded-2xl bg-white px-6 py-8 text-center shadow-sm ring-1 ring-slate-100 transition-shadow duration-200 hover:shadow-md"
              >
                <p className="text-2xl font-bold text-blue-700">{req.value}</p>
                <p className="mt-1 text-sm text-slate-600">{req.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative bg-blue-800 overflow-hidden">
        {/* Decorative semicircles */}
        <div className="pointer-events-none absolute -bottom-64 -left-64 h-[512px] w-[512px] rounded-full bg-blue-700/50" />
        <div className="pointer-events-none absolute -top-64 -right-64 h-[512px] w-[512px] rounded-full bg-blue-700/50" />

        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 lg:items-center">
            {/* Left side - Info */}
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl font-bold text-white sm:text-4xl">
                Get in touch today
              </h2>
              <p className="mt-3 text-lg text-blue-100 text-pretty">
                Fill out the form and we'll get back to you within minutes.
              </p>

              {/* Contact Info */}
              <div className="mt-10 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-200">Call us</p>
                    <p className="text-lg font-semibold text-white">
                      (786) 321-9346
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-200">
                      Business hours
                    </p>
                    <p className="text-lg font-semibold text-white">
                      Mon - Fri, 10AM - 6PM EST
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-200">
                      Email us
                    </p>
                    <p className="text-lg font-semibold text-white break-all sm:break-normal">
                      submissions@lloydfunding.com
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-200">
                      Our office
                    </p>
                    <p className="text-lg font-semibold text-white">
                      8400 NW 33rd St, Suite 310
                    </p>
                    <p className="text-sm text-blue-200">Miami, FL 33122</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Form */}
            <div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.target;
                  const formData = new FormData(form);

                  const data = {
                    firstName: formData.get("firstName"),
                    lastName: formData.get("lastName"),
                    email: formData.get("email"),
                    phone: formData.get("phone"),
                    company: formData.get("company"),
                    revenue: formData.get("revenue"),
                    amount: formData.get("amount"),
                  };

                  try {
                    const response = await fetch(
                      API.CONTACT_FORM,
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify(data),
                      }
                    );

                    if (response.ok) {
                      navigate("/thank-you");
                    } else {
                      throw new Error("Failed to submit");
                    }
                  } catch (error) {
                    alert("Oops! Something went wrong. Please try again.");
                  }
                }}
                className="rounded-2xl bg-white p-6 sm:p-8 shadow-xl"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {/* First Name */}
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-slate-700"
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors duration-200"
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors duration-200"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors duration-200"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors duration-200"
                    />
                  </div>

                  {/* Company Name */}
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors duration-200"
                    />
                  </div>

                  {/* Monthly Revenue */}
                  <div>
                    <label
                      htmlFor="revenue"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Monthly Revenue
                    </label>
                    <div className="relative">
                      <select
                        id="revenue"
                        name="revenue"
                        required
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
                      >
                        <option value="">Please Select</option>
                        <option value="5k-25k">$5K - $25K</option>
                        <option value="25k-50k">$25K - $50K</option>
                        <option value="50k-100k">$50K - $100K</option>
                        <option value="100k-250k">$100K - $250K</option>
                        <option value="250k-500k">$250K - $500K</option>
                        <option value="500k+">Over $500K</option>
                      </select>
                      <svg
                        className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m19 9-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Amount Requested */}
                  <div>
                    <label
                      htmlFor="amount"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Amount Requested
                    </label>
                    <div className="relative">
                      <select
                        id="amount"
                        name="amount"
                        required
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 pr-10 text-slate-900 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
                      >
                        <option value="">Please Select</option>
                        <option value="5k-25k">$5K - $25K</option>
                        <option value="25k-50k">$25K - $50K</option>
                        <option value="50k-100k">$50K - $100K</option>
                        <option value="100k-250k">$100K - $250K</option>
                        <option value="250k-500k">$250K - $500K</option>
                        <option value="500k+">Over $500K</option>
                      </select>
                      <svg
                        className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m19 9-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="sm:col-span-2 pt-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="smsConsent"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/20 flex-shrink-0"
                      />
                      <span className="text-xs leading-relaxed text-slate-500">
                        By submitting this form, I consent to receive text
                        messages and emails from Lloyd Funding regarding my
                        application and funding options. Message frequency
                        varies. Message and data rates may apply. Reply HELP for
                        support. Reply STOP to opt out.. View our{" "}
                        <Link
                          to="/privacy-policy"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Privacy Policy
                        </Link>{" "}
                        and{" "}
                        <Link
                          to="/terms-of-service"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Terms of Service
                        </Link>
                        .
                      </span>
                    </label>
                    <button
                      type="submit"
                      className="mt-3 w-full rounded-xl bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95"
                    >
                      Submit Application
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;